import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faRetweet, faTable, faTags, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Accordion, Alert, Button, Form } from "react-bootstrap";
import { IPage } from "../../../models/Interfaces";
import { MetaDataService } from "../../../services/MetaDataService";
import { KeywordService } from "../../../services/KeywordService";
import { RedirectService } from "../../../services/RedirectService";
import { useConfirm } from "../../../config/ConfirmProvider";
import { automaticMetaNote, isAutomaticMetaKey } from "../pagemetadata/pageMetadataKeys";
import styles from "./pageSeoPanel.module.scss";

export type SeoSection = "meta" | "keywords" | "redirects";
interface Entry { id: number; key?: string; value: string; automatic?: boolean; }
interface Props {
  page: IPage;
  section: SeoSection;
  onStateChange: (dirty: boolean, busy: boolean) => void;
}
const labels = { meta: "meta tag", keywords: "keyword", redirects: "redirect" };

export default function PageSeoPanel({ page, section, onStateChange }: Props) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState<Entry | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const mounted = useRef(true);
  const operation = useRef(false);
  const { requestConfirmation } = useConfirm();
  const [services] = useState(() => ({ meta: new MetaDataService(), keywords: new KeywordService(), redirects: new RedirectService() }));
  const label = labels[section];
  const original = entries.find(entry => entry.id === draft?.id);
  const dirty = !!draft && (draft.id === 0 || JSON.stringify(draft) !== JSON.stringify(original));

  useEffect(() => { onStateChange(dirty, busy); }, [dirty, busy, onStateChange]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    const load = async () => {
      try {
        let result: Entry[];
        if (section === "meta") result = (await services.meta.getPageMetaData(page.Id, true)).map(item => ({ id: item.Id, key: item.Key, value: item.Value, automatic: isAutomaticMetaKey(item.Key) }));
        else if (section === "keywords") result = (await services.keywords.getPageKeywords(page.Id, true)).map(item => ({ id: item.Id, value: item.Keyword }));
        else result = (await services.redirects.getRedirects(true)).filter(item => Number(item.PageId) === page.Id).map(item => ({ id: item.Id, value: item.RedirectUrl ?? item.SourceUrl ?? "" }));
        if (!cancelled) setEntries(result);
      } catch { if (!cancelled) setError(`Unable to load ${section === "meta" ? "meta tags" : section}. Please retry.`); }
      finally { if (!cancelled) setLoading(false); }
    };
    void load();
    return () => { cancelled = true; };
  }, [page.Id, section, services, reload]);

  const beginEdit = async (entry: Entry) => {
    if (operation.current) return;
    if (dirty && !await requestConfirmation("Discard your unsaved changes?", "Discard changes")) return;
    if (!mounted.current) return;
    setDraft({ ...entry }); setError(""); setNotice("");
  };
  const cancelEdit = async () => {
    if (operation.current) return;
    operation.current = true; setBusy(true);
    try {
      if (dirty && !await requestConfirmation("Discard your unsaved changes?", "Discard changes")) return;
      if (!mounted.current) return;
      setDraft(null); setError("");
    } finally { operation.current = false; if (mounted.current) setBusy(false); }
  };
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft || operation.current) return;
    const value = draft.value.trim(); const key = draft.key?.trim() ?? "";
    if (!value || (section === "meta" && (key.length < 3 || key.length > 50))) {
      setError(section === "meta" ? "Enter a meta key between 3 and 50 characters and a value." : `Enter a ${label}.`); return;
    }
    if (section === "meta" && (draft.automatic || isAutomaticMetaKey(key))) {
      setError("Keys starting with sb:auto: are generated from page content and cannot be edited here."); return;
    }
    operation.current = true; setBusy(true); setError(""); setNotice("");
    try {
      let saved: Entry | null = null;
      if (section === "meta") {
        const result = await services.meta.savePageMetaData({ Id: draft.id, PageId: page.Id, SiteId: page.SiteId, Key: key, Value: value });
        if (result) saved = { id: result.Id, key: result.Key, value: result.Value, automatic: isAutomaticMetaKey(result.Key) };
      } else if (section === "keywords") {
        const result = await services.keywords.saveKeyword({ Id: draft.id, PageId: page.Id, SiteId: page.SiteId, Keyword: value });
        if (result) saved = { id: result.Id, value: result.Keyword };
      } else {
        const result = await services.redirects.saveRedirect({ Id: draft.id, PageId: page.Id, SiteId: page.SiteId, RedirectUrl: value, Page: null });
        if (result) saved = { id: result.Id, value: result.RedirectUrl ?? result.SourceUrl ?? value };
      }
      if (!saved) throw new Error("Save failed");
      if (mounted.current) {
        const entry = saved;
        setEntries(prev => draft.id ? prev.map(item => item.id === draft.id ? entry : item) : [...prev, entry]);
        setDraft(null); setNotice(`${label[0].toUpperCase() + label.slice(1)} saved.`);
      }
    } catch { if (mounted.current) setError(`Unable to save this ${label}. Your changes have been kept. Please retry.`); }
    finally { operation.current = false; if (mounted.current) setBusy(false); }
  };
  const remove = async (entry: Entry) => {
    if (operation.current || entry.automatic) return;
    operation.current = true; setBusy(true);
    try {
      if (!await requestConfirmation(`Delete this ${label}? This cannot be undone.`, `Delete ${label}`) || !mounted.current) return;
      setError(""); setNotice("");
      const removed = section === "meta" ? await services.meta.deletePageMetaData(entry.id, page.Id)
        : section === "keywords" ? await services.keywords.deleteKeyword(entry.id, page.Id)
        : await services.redirects.deleteRedirect(entry.id);
      if (!removed) throw new Error("Delete failed");
      if (mounted.current) { setEntries(prev => prev.filter(item => item.id !== entry.id)); if (draft?.id === entry.id) setDraft(null); setNotice("Entry deleted."); }
    } catch { if (mounted.current) setError(`Unable to delete this ${label}. Please retry.`); }
    finally { operation.current = false; if (mounted.current) setBusy(false); }
  };

  const renderEntry = (entry: Entry) => (<article key={entry.id} className={styles.entry}>
      <div className={styles.entryText}>{entry.key && <strong>{entry.key}</strong>}<p>{entry.value}</p></div>
      <div className={styles.actions}>
        {!entry.automatic && <><Button variant="outline-secondary" size="sm" disabled={busy || !!draft} onClick={() => void beginEdit(entry)} aria-label={`Edit ${entry.key ?? entry.value}`}>Edit</Button>
        <Button variant="outline-danger" size="sm" disabled={busy || !!draft} onClick={() => void remove(entry)} aria-label={`Delete ${entry.key ?? entry.value}`}>Delete</Button></>}
        {entry.automatic && <span className="badge text-bg-secondary">Generated</span>}
      </div>
    </article>);

  const empty = !loading && !error && entries.length === 0 && !draft;
  const addButton = <Button variant="primary" disabled={loading || busy || !!draft || !!error} onClick={() => void beginEdit({ id: 0, value: "", ...(section === "meta" ? { key: "" } : {}) })}><FontAwesomeIcon icon={faPlus} className="me-2" />Add {label}</Button>;
  const title = section === "meta" ? "Meta tags" : section === "keywords" ? "Page keywords" : "Redirects to this page";
  const description = section === "meta" ? "Control this page’s descriptive metadata. Generated entries are read-only."
    : section === "keywords" ? "Keep the words and phrases associated with this page together."
    : "Send visitors from an old or alternative URL straight to this page.";
  return <section aria-label={section === "meta" ? "Page meta tags" : `Page ${section}`}>
    <div className={styles.toolbar}>
      <div><h3 className={styles.heading}>{title}</h3><p>{description}</p></div>
      {!empty && !draft && !loading && !error && addButton}
    </div>
    {empty && <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true"><FontAwesomeIcon icon={section === "meta" ? faTable : section === "keywords" ? faTags : faRetweet} /></span>
      <div className={styles.emptyContent}>
        <h4>{section === "redirects" ? "Keep old links working" : section === "meta" ? "Add this page’s metadata" : "Add words that describe this page"}</h4>
        <p>No {section === "meta" ? "meta tags" : section} have been added for this page.</p>
        <p className={styles.help}>{section === "redirects" ? "Has this page moved, or does it replace an old page? Add the old URL below so visitors reach the right place."
          : section === "meta" ? "Start with a description: a short summary of what visitors will find on this page."
          : "Add a relevant word or phrase, then save it. You can edit or remove entries here at any time."}</p>
        {section === "redirects" && <div className={styles.routeExample} aria-label="Example redirect">
          <div><small>Old URL (example)</small><code>/old-page</code></div>
          <FontAwesomeIcon icon={faArrowRight} aria-label="redirects to" />
          <div><small>This page</small><strong>{page.Title}</strong><code>{page.Url}</code></div>
        </div>}
        <div className="mt-3">{addButton}</div>
      </div>
    </div>}
    {loading && <div className={styles.loadingState} role="status" aria-live="polite" aria-busy="true">
      <div className={styles.loadingHeader}><span className={styles.emptyIcon} aria-hidden="true"><FontAwesomeIcon icon={section === "meta" ? faTable : section === "keywords" ? faTags : faRetweet} /></span>
        <div><strong>Loading {section === "meta" ? "meta tags" : section}…</strong><span>Getting this page’s saved entries.</span></div>
      </div>
      <div className={styles.skeletonList} aria-hidden="true">{[0, 1].map(row => <div className={styles.skeletonRow} key={row}><span /><span /></div>)}</div>
    </div>}
    {error && <Alert variant="danger">{error}{!draft && <Button variant="outline-secondary" className="ms-2" disabled={busy} onClick={() => setReload(value => value + 1)}>Retry</Button>}</Alert>}
    {notice && <p role="status" className="text-success">{notice}</p>}
    {draft && <Form onSubmit={save} className={styles.editor} aria-label={`Edit ${label}`}>
      <fieldset disabled={busy}>
        {section === "meta" && <Form.Group controlId="page-meta-key" className="mb-3"><Form.Label>Meta key</Form.Label><Form.Control autoFocus required minLength={3} maxLength={50} value={draft.key ?? ""} onChange={e => setDraft({ ...draft, key: e.target.value })} /></Form.Group>}
        <Form.Group controlId="page-seo-value" className="mb-3"><Form.Label>{section === "meta" ? "Meta value" : section === "keywords" ? "Keyword" : "Source URL"}</Form.Label>
          <Form.Control as={section === "meta" ? "textarea" : "input"} autoFocus={section !== "meta"} required value={draft.value} aria-describedby={section === "redirects" ? "page-redirect-help" : undefined} placeholder={section === "redirects" ? "/old-page" : undefined} onChange={e => setDraft({ ...draft, value: e.target.value })} />
        </Form.Group>
        {section === "redirects" && <div className={styles.destination}>
          <p id="page-redirect-help">Enter the old URL visitors use, for example <code>/old-contact</code>.</p>
          <span>Visitors will be sent to</span><strong>{page.Title}</strong><code>{page.Url}</code>
        </div>}
        <div className={styles.actions}><Button variant="outline-secondary" onClick={() => void cancelEdit()}>Cancel</Button><Button type="submit" variant="success">{busy ? "Saving…" : `Save ${label}`}</Button></div>
      </fieldset>
    </Form>}
    {section === "meta" && !loading && entries.length > 0 ? <Accordion alwaysOpen defaultActiveKey={["user"]} className={styles.metaGroups}>
      <Accordion.Item eventKey="user">
        <Accordion.Header>User added ({entries.filter(entry => !entry.automatic).length})</Accordion.Header>
        <Accordion.Body>
          <p className={styles.groupHelp}>Your custom meta tags. You can edit or delete these entries.</p>
          <div className={styles.list}>{entries.filter(entry => !entry.automatic).map(renderEntry)}</div>
          {!entries.some(entry => !entry.automatic) && <p className="mb-0">No user-added meta tags yet. Use Add meta tag to create one.</p>}
        </Accordion.Body>
      </Accordion.Item>
      <Accordion.Item eventKey="generated">
        <Accordion.Header>Generated ({entries.filter(entry => entry.automatic).length})</Accordion.Header>
        <Accordion.Body>
          <p className={styles.groupHelp}>{automaticMetaNote}</p>
          <div className={styles.list}>{entries.filter(entry => entry.automatic).map(renderEntry)}</div>
          {!entries.some(entry => entry.automatic) && <p className="mb-0">No generated meta tags are available for this page.</p>}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion> : <div className={styles.list}>{entries.map(renderEntry)}</div>}

  </section>;
}
